export interface ExternalUserMapping {
  organizationId: string;
  integration: string;
  externalUserId: string;
  /** WorkOS user id. */
  userId: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  /** Principal id of the last writer. */
  updatedBy: string;
}

/**
 * GET /v1/stations/{organizationId}/integrations/{integration}/mappings
 *
 * Returns only vendor ids that are already linked — there is no vendor
 * directory endpoint, so unmapped vendor officers never appear here and
 * `externalUserId` is the only vendor-side detail available (no name, badge
 * or email). Officer details come from joining `userId` against the station
 * users roster.
 */
export interface ListMappingsResponse {
  organizationId: string;
  /** Normalized lowercase. */
  integration: string;
  /** The parsed boolean, not the raw query-string value. */
  includeInactive: boolean;
  /** Present only when the `userId` filter was supplied. */
  userId?: string;
  /** Always equals `mappings.length` — the response is not paginated. */
  total: number;
  /** Sorted ascending by `externalUserId`. */
  mappings: ExternalUserMapping[];
}

export type ResyncScope = "unmapped_only" | "all" | "failed";

export interface ResyncResult {
  triggered: number;
  skipped: number;
  alreadyMapped: number;
}

export type VendorType = "axon" | "lenslock" | "watchguard";

/**
 * How the vendor roster was obtained, which decides what an *absent* officer
 * means:
 * - "directory"       — authoritative list (Axon, WatchGuard). Absence means
 *                       the officer does not exist.
 * - "evidence_sample" — LensLock, which has no directory endpoint. Derived
 *                       from 30 days of evidence, so it only shows who has
 *                       recent footage; absence proves nothing.
 */
export type VendorDirectorySource = "directory" | "evidence_sample";

/** One officer as the vendor knows them. */
export interface VendorDirectoryUser {
  /**
   * The identifier bodycam attributes evidence by, and the value to send as
   * `externalUserId` when creating a mapping. Matched byte-exact — never
   * normalize case.
   */
  id: string;
  /**
   * WatchGuard only: the officer's UUID. Deliberately NOT the mapping key —
   * `id` is.
   */
  userId?: string;
  name?: string;
  badge?: string;
  email?: string;
}

/**
 * GET /v1/stations/{organizationId}/integrations/bodycam/vendor-users
 *
 * The whole roster arrives in one response (no pagination) under a 10s vendor
 * scan budget, so this is a slow call on a large agency — never per keystroke.
 * Only `source: "directory"` together with `complete: true` licenses telling
 * an admin an ID doesn't exist; otherwise absence just means "not in what we
 * could see".
 */
export interface ListVendorUsersResponse {
  organizationId: string;
  integration: "bodycam";
  vendorType: VendorType;
  source: VendorDirectorySource;
  /** false when the page cap or the 10s budget truncated the scan. */
  complete: boolean;
  /** Always === users.length. No pagination. */
  total: number;
  users: VendorDirectoryUser[];
}

/** The station has no active bodycam binding — a setup state, not a failure. */
export const BODYCAM_NOT_CONFIGURED = "bodycam_integration_not_configured";
/** The bound vendor has no directory loader on the backend. */
export const BODYCAM_VENDOR_UNSUPPORTED = "bodycam_vendor_unsupported";
